import { useState } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import { focusFirstInvalidField } from "../lib/focus-first-invalid";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";
import { StateCombobox } from "./ui/StateCombobox";
import { normalizeInput, normalizeUsZip } from "../lib/normalize-input";
import { isPuertoRico } from "../lib/puerto-rico-check";

const PO_BOX_PATTERN =
  /\b(P\.?\s*O\.?\s*Box|Post\s*Office\s*Box|P\.?\s*O\.?\s*B\.?)\s*\d*\b/i;

function isPOBox(...addressLines: string[]) {
  const combined = addressLines
    .filter(Boolean)
    .map((s) => String(s).trim())
    .join(" ");
  return PO_BOX_PATTERN.test(combined);
}


interface PatientShippingInfoProps {
  onContinue: (data: ShippingInfoData) => void;
  onBack: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  initialAddress?: string;
  initialAddress2?: string;
  initialCity?: string;
  initialZipCode?: string;
  initialState?: string;
  initialFirstName?: string;
  initialLastName?: string;
  customerId?: string;
  patientId?: string;
  email?: string;
  phone?: string;
}

export interface ShippingInfoData {
  address: string;
  address2: string;
  city: string;
  state: string;
  zip_code: string;
}


export function PatientShippingInfo({
  onContinue,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  initialAddress = "",
  initialAddress2 = "",
  initialCity = "",
  initialZipCode = "",
  initialState = "",
  initialFirstName = "",
  initialLastName = "",
  customerId,
  patientId,
  phone,
}: PatientShippingInfoProps) {
  const t: any = getNamespaceTranslations(language, "patientShippingInfo" as any);

  const [form, setForm] = useState({
    address: initialAddress,
    address2: initialAddress2,
    city: initialCity,
    state: initialState,
    zip_code: initialZipCode,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const isPR = isPuertoRico(form.state);
  const hasPOBox = isPOBox(form.address, form.address2);
  const poBoxErrorMessage =
    t.poBoxError ||
    (language === "es"
      ? "No podemos enviar a direcciones de apartados postales (P.O. Box). Por favor ingresa una dirección física."
      : "We cannot ship to P.O. Box addresses. Please enter a physical street address.");
  const fieldRequired = t.fieldRequired || (language === "es" ? "Este campo es obligatorio" : "This field is required");
  const zipInvalid =
    t.zipInvalidError ||
    (language === "es"
      ? "Ingresa un código postal de 5 dígitos válido"
      : "Please enter a valid 5-digit zip code");

  const clearFieldError = (name: string) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === "checkbox" && "checked" in e.target ? e.target.checked : undefined;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    clearFieldError(name);
  };

  const validate = (zipNormalized: string): string | null => {
    const errors: Record<string, string> = {};
    if (!form.address.trim()) errors.address = fieldRequired;
    else if (hasPOBox) errors.address = poBoxErrorMessage;
    if (!form.city.trim()) errors.city = fieldRequired;
    if (!form.state.trim()) errors.state = fieldRequired;
    else if (isPR) errors.state = t.puertoRicoError;
    if (!form.zip_code.trim()) errors.zip_code = fieldRequired;
    else if (zipNormalized.length !== 5) errors.zip_code = zipInvalid;
    setFieldErrors(errors);
    if (Object.keys(errors).length === 0) return null;
    if (errors.address) return "address";
    if (errors.city) return "city";
    if (errors.state) return "state-select";
    if (errors.zip_code) return "zip_code";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const zipNormalized = normalizeUsZip(form.zip_code);
    const firstInvalidId = validate(zipNormalized);
    if (firstInvalidId) {
      focusFirstInvalidField([firstInvalidId]);
      return;
    }
    // Reflect ZIP+4 → 5-digit in the field so users see what we saved
    if (form.zip_code !== zipNormalized) {
      setForm((prev) => ({ ...prev, zip_code: zipNormalized }));
    }
    setIsLoading(true);

    try {
      const cityNormalized = normalizeInput(form.city.trim());
      const shippingData: ShippingInfoData = {
        ...form,
        city: cityNormalized,
        zip_code: zipNormalized,
      };

      const payload = {
        customer_id: customerId,
        patient_id: patientId,
        first_name: initialFirstName,
        last_name: initialLastName,
        address: form.address,
        address2: form.address2,
        city: cityNormalized,
        state: form.state,
        zip_code: zipNormalized,
        ...(phone && { phone: phone.replace(/\D/g, '') }), // Include phone if available (from registration)
      };

      const response = await fetch("/apps/rx/customer/address", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        onContinue(shippingData);
      } else {
        alert("Failed to save shipping information. Please try again.");
      }
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const addressError = fieldErrors.address || (hasPOBox ? poBoxErrorMessage : "");
  const cityError = fieldErrors.city || "";
  const zipError = fieldErrors.zip_code || "";
  const stateError =
    fieldErrors.state ||
    (isPR ? t.puertoRicoError : undefined);

  return (
    <div className="w-full h-full min-h-screen flex flex-col bg-white">
      <Header logoUrl={logoUrl} onBack={onBack} onClose={onClose} />

    <div className="flex-1 flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
      <div className="max-w-[640px] flex flex-col h-[calc(100%-40px)] mx-auto px-5 py-8 lg:px-4 w-full mb-0">
        {currentStep !== undefined &&
          totalSteps !== undefined &&
          sectionIndex !== undefined &&
          totalSections !== undefined &&
          sectionId !== undefined && (
          <ProgressBar
            currentStep={currentStep}
            totalSteps={totalSteps}
            sectionIndex={sectionIndex}
            totalSections={totalSections}
            sectionId={sectionId}
            language={language}
          />
        )}

        <h2
          className="text-2xl font-normal leading-[30px] tracking-[0px] mb-6 mt-[30px]"
          style={{ fontFamily: "TT Commons Pro" }}
        >
          {t.title}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Address */}
          <div>
            <label htmlFor="address" className="block mb-3 text-sm font-medium required-label">
              {t.addressLabel}
            </label>
            <input
              id="address"
              type="text"
              name="address"
              autoComplete="street-address"
              value={form.address}
              onChange={handleChange}
              maxLength={35}
              className={`w-full !border-0 ring-1 ring-inset rounded-[6px] px-4 !py-[12.5] sm:!py-[10.5] leading-6 text-[16px] bg-white ${
                addressError ? "ring-[#E34D52]" : "ring-gray-300"
              }`}
              placeholder={t.addressPlaceholder}
              aria-required="true"
              aria-invalid={!!addressError}
              data-testid="delivery_input-address"
            />
            {addressError ? (
              <p className="mt-2 text-sm text-[#E34D52]" role="alert">
                {addressError}
              </p>
            ) : null}
          </div>

          {/* Address Line 2 (Apartment, Unit, etc.) */}
          <div>
            <label htmlFor="address2" className="block mb-3 text-sm font-medium">
              {t.address2Label}
            </label>
            <input
              id="address2"
              type="text"
              name="address2"
              autoComplete="address-line2"
              value={form.address2}
              onChange={handleChange}
              maxLength={35}
              className="w-full !border-0 ring-1 ring-inset ring-gray-300 rounded-[6px] px-4 !py-[12.5] sm:!py-[10.5] leading-6 text-[16px] bg-white"
              placeholder={t.address2Placeholder}
              data-testid="delivery_input-address2"
            />
          </div>

          {/* City Name */}
          <div>
            <label htmlFor="city" className="block mb-3 text-sm font-medium required-label">
              {t.cityLabel}
            </label>
            <input
              id="city"
              type="text"
              name="city"
              autoComplete="address-level2"
              value={form.city}
              onChange={handleChange}
              className={`w-full !border-0 ring-1 ring-inset rounded-[6px] px-4 !py-[12.5] sm:!py-[10.5] leading-6 text-[16px] bg-white ${
                cityError ? "ring-[#E34D52]" : "ring-gray-300"
              }`}
              placeholder={t.cityPlaceholder}
              aria-required="true"
              aria-invalid={!!cityError}
              data-testid="delivery_input-city"
            />
            {cityError ? (
              <p className="mt-2 text-sm text-[#E34D52]" role="alert">
                {cityError}
              </p>
            ) : null}
          </div>

          {/* State Selector */}
          <StateCombobox
            value={form.state}
            onChange={(state) => {
              setForm((prev) => ({ ...prev, state }));
              clearFieldError("state");
            }}
            label={t.stateLabel}
            placeholder={t.statePlaceholder}
            errorMessage={stateError}
            language={language}
            className="mb-3"
            testId="delivery_input-state"
          />

          {/* Zip Code */}
          <div>
            <label htmlFor="zip_code" className="block mb-3 text-sm font-medium required-label">
              {t.zipLabel}
            </label>
            <input
              id="zip_code"
              type="text"
              name="zip_code"
              autoComplete="postal-code"
              inputMode="numeric"
              value={form.zip_code}
              onChange={handleChange}
              className={`w-full !border-0 ring-1 ring-inset rounded-[6px] px-4 !py-[12.5] sm:!py-[10.5] leading-6 text-[16px] bg-white ${
                zipError ? "ring-[#E34D52]" : "ring-gray-300"
              }`}
              placeholder={t.zipPlaceholder}
              aria-required="true"
              aria-invalid={!!zipError}
              data-testid="delivery_input-zip"
            />
            {zipError ? (
              <p className="mt-2 text-sm text-[#E34D52]" role="alert">
                {zipError}
              </p>
            ) : null}
          </div>

          {/* Submit Button — always full colour; validate on submit */}
          <Button
            type="submit"
            disabled={isLoading}
            loading={isLoading}
            data-testid="delivery-continue"
            className={`w-full h-[50px] text-white rounded-full flex items-center justify-center transition-colors mt-6 bg-[#2D55FB] ${
              isLoading ? "cursor-not-allowed" : "hover:bg-[#2347E8]"
            }`}
          >
            {t.continueButton}
          </Button>
        </form>
      </div>
    </div>
    </div>
  );
}
